// src/components/SearchWithTabs.jsx
import React, { useState, useEffect, useRef } from "react";
import { Input, Tabs, Card, Spin, Tag, Row, Col, Button } from "antd";
import {
  ArrowRightOutlined,
  SlidersOutlined,
  FileTextOutlined,
  DollarCircleOutlined,
  BulbOutlined,
  ReadOutlined,
  RiseOutlined,
  InboxOutlined,
  ClusterOutlined,
  LineChartOutlined,
  TableOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { API_URL } from "./SEO/seo_fetch";

const { Search } = Input;

const SearchWithTabs = ({
  /* ----- chat‑bar state & handlers from parent ----- */
  isFirstMessage,
  isMobile,
  newMessage, // ← we use this as the input value
  setNewMessage,
  handleChange,
  handleSendMessage,
  skeletonLoading,
  streamActive,
  handleStreamingStop,
  isActive,
}) => {
  const containerRef = useRef(null);

  /* ------------- component‑specific state ------------- */
  const [activeTab, setActiveTab] = useState("ideas");
  const [isFocused, setIsFocused] = useState(true);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);

  /* ------------- static Ideas & Wiki lists ------------- */
  const mockData = {
    ideas: [
      {
        label: "Top 3 Undervalued companies as per Analysts",
        message:
          "Identify top 3 companies with an analyst price target high that exceeds the current stock price by more than 50%",
        icon: <SlidersOutlined style={{ color: "#5b8c00" }} />,
      },
      {
        label: "Summarize Earning call of Nvidia",
        message: "Summarize Earning call of Nvidia",
        icon: <FileTextOutlined style={{ color: "#fa8c16" }} />,
      },
      {
        label: "What is fair value of Costco?",
        message: "What is fair value of Costco?",
        icon: <DollarCircleOutlined style={{ color: "#fd6f7f" }} />,
      },
      {
        label: "Fundamental Growth stocks",
        message:
          "Identify companies with revenue growth above 10% annually for the past three years and a gross profit margin exceeding 20%.",
        icon: <BulbOutlined style={{ color: "#9254de" }} />,
      },
      {
        label: "Latest news of Amazon",
        message: "Latest news of Amazon",
        icon: <ReadOutlined style={{ color: "#f759ab" }} />,
      },
      {
        label: "Companies making new 52 weeks highs",
        message:
          "Companies trading within 10% of 52 weeks high with market cap more than 10Bn ",
        icon: <RiseOutlined style={{ color: "#fbc396" }} />,
      },
    ],
    "stocks-wiki": [
      "What does ROIC mean?",
      "What is a PEG Ratio?",
      "Why Insider Trading is a Key Signal",
    ],
  };

  /* ------------- tab → API category map ------------- */
  const categoryMapping = {
    symbols: "symbol",
    sectors: "sector",
    analysis: "company_analysis",
    screens: "screener",
    "earning-call-summary": "earning_call",
  };

  /* ------------- load suggestions on focus/tab change ------------- */
  // Open ideas tab and fetch results on initial render
  useEffect(() => {
    setIsFocused(true);
    setActiveTab("ideas");
    fetchResults("");
  }, []);

  // Handle tab switching or focus events afterward
  useEffect(() => {
    if (isFocused && categoryMapping[activeTab]) fetchResults("");
    else setResults([]);
    setNewMessage?.("");
  }, [activeTab, isFocused]);

  /* ------------- close dropdown on outside click ------------- */
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target))
        setIsFocused(true);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  /* ------------- Lambda search helper ------------- */
  const fetchResults = async (val) => {
    const cat = categoryMapping[activeTab];
    if (!cat) return;
    setLoading(true);
    try {
      const url = `https://wmsxju1yh0.execute-api.us-east-1.amazonaws.com/prod/search?q=${encodeURIComponent(
        val
      )}&category=${cat}`;
      const res = await axios.get(url);
      let data = Array.isArray(res.data)
        ? res.data
        : JSON.parse(res.data?.body || "[]");
      if (val === "") data = data.sort(() => 0.5 - Math.random()).slice(0, 10);
      setResults(data);
    } catch {
      setResults([]);
    }
    setLoading(false);
  };

  /* ------------- search‑box “Enter” ------------- */
  const handleSearch = (val) => {
    setNewMessage?.(val);
    if (categoryMapping[activeTab]) fetchResults(val);
  };

  /* ------------- decide dropdown items ------------- */
  const filteredResults = () => {
    if (categoryMapping[activeTab]) return results;
    if (activeTab === "ideas") return mockData.ideas;
    const list = mockData[activeTab] || [];
    if (!newMessage) return list;
    return list.filter((t) =>
      t?.toLowerCase()?.includes(newMessage?.toLowerCase())
    );
  };

  /* ------------- styles ------------- */
  const ideaTile = {
    border: "1px solid #d9d9d9",
    borderRadius: 8,
    padding: ".5vw .8vw",
    minHeight: 52,
    cursor: "pointer",
    transition: "all 0.15s ease",
    display: "flex",
    alignItems: "center",
    gap: 12,
    color: "#595959",
    fontSize: 14,
    fontWeight: 500,
  };
  const tileHover = { borderColor: "#40a9ff", color: "#141414" };
  const cardStyle = {
    marginBottom: 8,
    borderRadius: 6,
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    transition: "transform 0.15s ease",
  };
  const lift = (e, up) =>
    (e.currentTarget.style.transform = up ? "scale(1.02)" : "none");

  /* ------------- render ------------- */
  return (
    <>
      <div
        ref={containerRef}
        style={{
          background: "#fff",
          padding: 20,
          borderRadius: 10,
          width: "80%",
          marginTop: "10%",
          /* ↓ no scrolling on the whole card */
          /* maxHeight & overflowY removed */
          boxShadow: "0 .1vw 1vw rgba(0,0,0,0.1)",
        }}
      >
        <Search
          value={newMessage}
          placeholder="Search ideas, symbols, sectors…"
          enterButton={
            <Button
              style={{
                backgroundColor: isActive ? "#28a745" : "#d9d9d9",
                borderColor: isActive ? "#28a745" : "#d9d9d9",
                color: "white",
                transition: "all 0.3s ease",
                animation: isActive ? "softGlowOnce 0.7s ease-out" : "none",
              }}
              icon={
                <ArrowRightOutlined
                  style={{
                    color: isActive ? "white" : "grey",
                    fontSize: "18px",
                    fontWeight: "bold",
                    transition: "color 0.3s ease",
                  }}
                />
              }
              disabled={!isActive || skeletonLoading}
            />
          }
          size="large"
          disabled={skeletonLoading}
          onSearch={(val) => {
            setNewMessage(val);
            handleSendMessage();
          }}
          onChange={(e) => {
            setNewMessage(e.target.value);
            handleChange?.(e);
            if (categoryMapping[activeTab]) fetchResults(e.target.value);
          }}
          onPressEnter={(e) => {
            if (!e.shiftKey) {
              e.preventDefault();
              handleSendMessage();
            }
          }}
          onFocus={() => {
            setIsFocused(true);
          }}
          style={{ marginBottom: 12 }}
        />

        {isFocused && (
          <>
            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              centered
              size="medium"
              tabBarGutter={32}
              tabBarStyle={{ fontWeight: 500, marginBottom: 20 }}
              items={[
                {
                  key: "ideas",
                  label: (
                    <span>
                      <BulbOutlined /> Ideas
                    </span>
                  ),
                },
                {
                  key: "symbols",
                  label: (
                    <span>
                      <InboxOutlined /> Symbols
                    </span>
                  ),
                },
                {
                  key: "sectors",
                  label: (
                    <span>
                      <ClusterOutlined /> Sectors
                    </span>
                  ),
                },
                {
                  key: "analysis",
                  label: (
                    <span>
                      <LineChartOutlined /> Company&nbsp;Analysis
                    </span>
                  ),
                },
                {
                  key: "screens",
                  label: (
                    <span>
                      <TableOutlined /> Screens
                    </span>
                  ),
                },
                {
                  key: "earning-call-summary",
                  label: (
                    <span>
                      <FileTextOutlined /> Earning‑Call
                    </span>
                  ),
                },
                {
                  key: "stocks-wiki",
                  label: (
                    <span>
                      <ReadOutlined /> Stocks&nbsp;Wiki
                    </span>
                  ),
                },
              ]}
            />

            <div
              style={{
                marginTop: 16,
                minHeight: 180,
                maxHeight: isMobile ? "50vh" : "40vh", // viewport slice
                overflowY: "auto", // ← only this area scrolls
                paddingRight: 4, // keep scrollbar off text
              }}
            >
              {" "}
              {loading ? (
                <div style={{ textAlign: "center", padding: 40 }}>
                  <Spin tip="Searching…" size="large" />
                </div>
              ) : activeTab === "ideas" ? (
                /* prettier grid for Ideas */
                <Row gutter={[12, 12]}>
                  {filteredResults().map((idea, i) => (
                    <Col xs={24} sm={12} key={i}>
                      <div
                        style={ideaTile}
                        onMouseEnter={(e) =>
                          Object.assign(e.currentTarget.style, tileHover)
                        }
                        onMouseLeave={(e) =>
                          Object.assign(e.currentTarget.style, ideaTile)
                        }
                        /* 👇 populate the bar, don’t open a new page */
                        onClick={() => {
                          setNewMessage?.(idea?.message ?? "");
                          // setIsFocused(false); // hide suggestions (optional)
                        }}
                      >
                        {idea?.icon}
                        {idea?.label}
                      </div>
                    </Col>
                  ))}
                </Row>
              ) : (
                /* other tabs */
                filteredResults().map((item, i) => {
                  if (activeTab === "stocks-wiki") {
                    return (
                      <Card
                        key={i}
                        hoverable
                        style={cardStyle}
                        onMouseEnter={(e) => lift(e, true)}
                        onMouseLeave={(e) => lift(e, false)}
                        /* 👇 copy the wiki question into the bar */
                        onClick={() => {
                          setNewMessage?.(item ?? "");
                        }}
                      >
                        <div style={{ color: "#333" }}>{item}</div>
                      </Card>
                    );
                  }

                  if (activeTab === "symbols") {
                    return (
                      <Card
                        key={i}
                        hoverable
                        style={cardStyle}
                        onMouseEnter={(e) => lift(e, true)}
                        onMouseLeave={(e) => lift(e, false)}
                        onClick={() =>
                          window.open(`/${item?.symbol}`, "_blank")
                        }
                      >
                        {/* <div style={{ fontSize: 16, fontWeight: 600 }}>
                          {item?.symbol}
                        </div> */}
                        <div
                          style={{
                            color: "#333",
                            margin: "4px 0",
                            fontSize: 16,
                            fontWeight: 500,
                          }}
                        >
                          {item?.company} - {item?.symbol}
                        </div>
                        <div style={{ marginTop: 4 }}>
                          {item?.tags
                            ?.split(",")
                            ?.map((t) => t.trim())
                            ?.map((t) => (
                              <Tag key={t} style={{ marginBottom: 4 }}>
                                {t}
                              </Tag>
                            ))}
                        </div>
                      </Card>
                    );
                  }

                  if (activeTab === "sectors") {
                    const raw = item?.sector;
                    const slug = raw?.toLowerCase()?.replace(/\s+/g, "-");
                    const disp =
                      raw?.charAt?.(0)?.toUpperCase?.() + raw?.slice?.(1);

                    const handleSectorClick = async () => {
                      const blogPath = `/blogs/${slug}/sector-analysis-of-${slug}-and-what-it-means-for-investors`;

                      const fallbackSearchQuery = `Analyze Top Companies in ${disp} Sector`;
                      const fallbackParams = new URLSearchParams({
                        question: fallbackSearchQuery,
                      }).toString();

                      try {
                        const resp = await axios.post(
                          API_URL,
                          {
                            queryStringParameters: {
                              url: `sector-analysis-of-${slug}-and-what-it-means-for-investors`,
                            },
                          },
                          { headers: { "Content-Type": "application/json" } }
                        );

                        const rawData = JSON.parse(resp.data.body);
                        if (rawData?.description) {
                          window.open(blogPath, "_blank");
                          return;
                        }
                      } catch (err) {
                        console.warn(
                          "SEO check failed, falling back to search:",
                          err
                        );
                      }

                      // Open fallback in the current tab if SEO content isn't available
                      window.location.href = `/?${fallbackParams}`;
                    };

                    return (
                      <Card
                        key={i}
                        hoverable
                        style={cardStyle}
                        onMouseEnter={(e) => lift(e, true)}
                        onMouseLeave={(e) => lift(e, false)}
                        onClick={handleSectorClick}
                      >
                        <div style={{ fontSize: 16, fontWeight: 500 }}>
                          {disp}
                        </div>
                      </Card>
                    );
                  }

                  if (activeTab === "analysis") {
                    return (
                      <Card
                        key={i}
                        hoverable
                        style={cardStyle}
                        onMouseEnter={(e) => lift(e, true)}
                        onMouseLeave={(e) => lift(e, false)}
                        onClick={() =>
                          window.open(
                            `/blogs/${item?.symbol}/${item?.url}`,
                            "_blank"
                          )
                        }
                      >
                        <div style={{ fontSize: 16, fontWeight: 500 }}>
                          {item?.companyName} - {item?.symbol}
                        </div>
                        <div style={{ color: "#333", margin: "4px 0" }}>
                          {item?.title}
                        </div>
                        {/* {item?.faq_headline && (
                          <div style={{ fontSize: 14, color: "#888" }}>
                            {item?.faq_headline}
                          </div>
                        )} */}
                      </Card>
                    );
                  }

                  if (activeTab === "screens") {
                    return (
                      <Card
                        key={i}
                        hoverable
                        style={cardStyle}
                        onMouseEnter={(e) => lift(e, true)}
                        onMouseLeave={(e) => lift(e, false)}
                        onClick={() => {
                          setNewMessage?.(item?.description ?? "");
                        }}
                      >
                        <div style={{ fontSize: 16, fontWeight: 500 }}>
                          {item?.title}
                        </div>
                        <div style={{ fontSize: 14, color: "#888" }}>
                          {item?.description}
                        </div>
                      </Card>
                    );
                  }

                  if (activeTab === "earning-call-summary") {
                    const slug = item?.symbol?.toLowerCase?.();
                    return (
                      <Card
                        key={i}
                        hoverable
                        style={cardStyle}
                        onMouseEnter={(e) => lift(e, true)}
                        onMouseLeave={(e) => lift(e, false)}
                        onClick={() =>
                          window.open(
                            `/blogs/${item?.symbol}/${slug}-stock-latest-earning-call-summary`,
                            "_blank"
                          )
                        }
                      >
                        <div style={{ fontSize: 16, fontWeight: 500 }}>
                          {item?.symbol} – {item?.companyName}
                        </div>
                        <div style={{ fontSize: 14, color: "#888" }}>
                          {item?.faq_headline}
                        </div>
                      </Card>
                    );
                  }

                  return null;
                })
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default SearchWithTabs;
